import { useSession } from "~/contexts/auth-context";
import { useSearch } from "~/hooks/search/use-search";
import {
  FilterType,
  SearchFilterComparisonOperator,
} from "~/hooks/search/types";
import { getDefaultCodesKind } from "~/shared/constants/codes-kind";

export type MetadataGapFilterName =
  | "keywordCount"
  | "descriptionWordCount"
  | "themaCodeCount"
  | "bisacCodeCount";

const thresholds: Record<MetadataGapFilterName, number> = {
  keywordCount: 5,
  descriptionWordCount: 300,
  themaCodeCount: 3,
  bisacCodeCount: 3,
};

const countQuery = (filterName: MetadataGapFilterName) => ({
  filters: {
    [filterName]: {
      value: thresholds[filterName],
      comparisonOperator: SearchFilterComparisonOperator.LESS,
      type: FilterType.NUMERIC,
    },
  },
  pageSize: 0,
});

export const useMetadataGapCounts = <
  Card extends { filterName: MetadataGapFilterName },
>(
  cards: Card[],
) => {
  const { organisation } = useSession();
  const usesThemaCodes = getDefaultCodesKind(organisation.settings) === "THEMA";

  const searchByFilter = {
    keywordCount: useSearch(countQuery("keywordCount")),
    descriptionWordCount: useSearch(countQuery("descriptionWordCount")),
    themaCodeCount: useSearch(countQuery("themaCodeCount"), {
      enabled: usesThemaCodes,
    }),
    bisacCodeCount: useSearch(countQuery("bisacCodeCount")),
  };

  const searches = cards
    .filter((card) => usesThemaCodes || card.filterName !== "themaCodeCount")
    .map((card) => ({ card, search: searchByFilter[card.filterName] }));

  const isLoading = searches.some(({ search }) => search.isPending);
  const cardsWithGaps = searches.flatMap(({ card, search }) => {
    const count = search.data?.found ?? 0;
    return count > 0
      ? [{ ...card, threshold: thresholds[card.filterName], count }]
      : [];
  });
  const isAllCaughtUp =
    cardsWithGaps.length === 0 &&
    searches.every(({ search }) => search.isSuccess);

  return { isLoading, cardsWithGaps, isAllCaughtUp };
};
